// Uretilen videoyu bir liste taslagina baglar.
//
// Video yerel ComfyUI'de duruyor (localhost /view adresi); API oraya
// ulasamaz. Bu yuzden tarayici videoyu indirir, /upload-video ile kalici
// depoya yukler ve taslagin videoUrl alanina yazar. Etsy'ye yukleme yayin
// sirasinda mevcut akisla yapilir.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Film, Loader2, Check } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

interface DraftRow {
  id: string;
  title: string;
  status: string;
  etsyListingId?: string | null;
  videoUrl?: string | null;
}

const STATUS_TR: Record<string, string> = {
  draft: "Taslak", ready: "Hazır", scheduled: "Planlı", published: "Yayında", archived: "Arşiv", builder: "Builder",
};

export function AttachVideoToDraft({ videoUrl, preferIds = [] }: { videoUrl: string; preferIds?: string[] }) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<DraftRow[] | null>(null);
  const [draftId, setDraftId] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<DraftRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function openPanel() {
    setOpen(true);
    if (drafts) return;
    try {
      const res = await fetch(`${getBackendUrl()}/api/listing-drafts?light=1`, { headers: authHeaders() });
      const body = await res.json().catch(() => ({}));
      const rows = (body.data ?? []) as DraftRow[];
      // Secilen urunun taslaklari en ustte
      rows.sort((a, b) => Number(preferIds.includes(b.id)) - Number(preferIds.includes(a.id)));
      setDrafts(rows);
      setDraftId(rows.find((r) => preferIds.includes(r.id))?.id ?? "");
    } catch {
      setError("Taslaklar alınamadı");
      setDrafts([]);
    }
  }

  async function attach() {
    const draft = drafts?.find((d) => d.id === draftId);
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const vid = await fetch(videoUrl);
      if (!vid.ok) throw new Error("Video ComfyUI'den okunamadı");
      const blob = await vid.blob();
      const fd = new FormData();
      fd.append("video", new File([blob], "listing-video.mp4", { type: blob.type || "video/mp4" }));
      // FormData icin Content-Type'i tarayici koyar (boundary ile)
      const { "Content-Type": _ct, ...headers } = authHeaders();
      void _ct;
      const up = await fetch(`${getBackendUrl()}/api/listing-drafts/upload-video`, { method: "POST", headers, body: fd });
      const upBody = await up.json().catch(() => ({}));
      if (!up.ok) throw new Error(upBody.error || "Video yüklenemedi");
      const patch = await fetch(`${getBackendUrl()}/api/listing-drafts/${draft.id}`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify({ videoUrl: upBody.data.url }),
      });
      if (!patch.ok) throw new Error((await patch.json().catch(() => ({}))).error || "Taslağa yazılamadı");
      setDone(draft);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eklenemedi");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <p className="flex flex-wrap items-center gap-1 text-sm text-emerald-700">
        <Check className="h-4 w-4" /> "{done.title.slice(0, 50)}" taslağına eklendi.
        {done.etsyListingId
          ? " Liste zaten Etsy'de: Listing Builder'da bu taslakta \"Görselleri Yenile\" deyin, video da yüklenir."
          : " Yayınlarken Etsy'ye yüklenir."}
        <Link to={`/listing-builder?id=${done.id}`} className="text-indigo-600 underline">Taslağı aç</Link>
      </p>
    );
  }

  if (!open) {
    return (
      <button onClick={() => void openPanel()}
        className="inline-flex items-center gap-2 text-sm px-3 py-1.5 border rounded-lg hover:bg-gray-50">
        <Film className="w-4 h-4" /> Taslağa ekle
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-gray-50 p-2 text-sm">
      {!drafts ? (
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      ) : (
        <select value={draftId} onChange={(e) => setDraftId(e.target.value)} className="min-w-0 flex-1 rounded border px-2 py-1">
          <option value="">Taslak seç…</option>
          {drafts.map((d) => (
            <option key={d.id} value={d.id}>
              {preferIds.includes(d.id) ? "★ " : ""}{d.title.slice(0, 70)} · {STATUS_TR[d.status] ?? d.status}{d.videoUrl ? " · videosu var" : ""}
            </option>
          ))}
        </select>
      )}
      <button onClick={() => void attach()} disabled={busy || !draftId}
        className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1 text-white disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Film className="h-3.5 w-3.5" />} Ekle
      </button>
      <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-700">Vazgeç</button>
      {error && <p className="w-full text-xs text-red-600">{error}</p>}
      {drafts?.length === 0 && !error && <p className="w-full text-xs text-gray-500">Taslak yok — önce Liste Denetimi'nden taslak oluşturun.</p>}
    </div>
  );
}
